import {
  useEffect,
  ReactNode,
  useContext,
  useState,
  ChangeEvent,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  Stack,
  useTheme,
  Button,
  ListSubheader,
  Divider,
  capitalize,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ImportWizardContext } from "components/importer/ImportWizardContext";
import SystemFieldListItem from "./SystemFieldListItem";
import ImportAlert, { ImportAlertProps } from "components/importer/ImportAlert";
import AuturaDropdown from "components/inputs/AuturaDropdown";
import { getContrastColor } from "context/themeContext";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { ValidationWidget } from "./ValidationWidget";
import { WIZARD_ACTION, DataWidget } from "types";
import DataPreviewTable from "./DataPreviewTable";
import { ExtractVehicleWidget } from "./ExtractVehicleWidget";
import useAlerts from "./useImportAlerts";
import { GenerateDataWidget } from "./GenerateDataWidget";
import { CombineFieldsWidget } from "./CombineFieldsWidget";

dayjs.extend(customParseFormat);

interface MapStepProps {
  maxHeight: number | string;
}

const MapStep = <F extends string>({ maxHeight }: MapStepProps): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { state, dispatch, rowsWithInvalidValues, fieldMetadata } =
    useContext(ImportWizardContext);

  const ALERTS = useAlerts({ entityName: state.entityName });
  const [dataWidget, setDataWidget] = useState<DataWidget>("none");

  const [centerAlerts, setCenterAlerts] = useState<ImportAlertProps[]>([
    ALERTS.MAP_TUTORIAL,
  ]);
  const [validationAlerts, setValidationAlerts] = useState<ImportAlertProps[]>(
    [],
  );
  const [metadataAlerts, setMetadataAlerts] = useState<ImportAlertProps[]>([]);

  const [selectedOption, setSelectedOption] = useState<string>("");

  // get the fields for each category
  const {
    generalFields,
    vehicleFields,
    contactFields,
    requiredFields,
    locationFields,
  } = useMemo(() => {
    const general: F[] = [];
    const vehicle: F[] = [];
    const contact: F[] = [];
    const location: F[] = [];

    const required: F[] = [];

    state.entityFields.forEach((field: F) => {
      const metadata = fieldMetadata[field];

      if (metadata.category === "entity") {
        general.push(field);
      } else if (metadata.category === "vehicle") {
        vehicle.push(field);
      } else if (metadata.category === "contact") {
        contact.push(field);
      } else if (metadata.category === "location") {
        location.push(field);
      }
      if (metadata.required) {
        required.push(field);
      }
    });

    // console.log(general, vehicle, contact, location, required);

    return {
      generalFields: general,
      vehicleFields: vehicle,
      contactFields: contact,
      requiredFields: required,
      locationFields: location,
    };
  }, [state.entityFields, fieldMetadata]);

  // commonly derived state
  const csvHeader = state.selectedSystemField
    ? state.columnMappings[state.selectedSystemField]
    : null;
  const mappingComplete =
    state.confirmedFields.length === state.entityFields.length;

  const confirmedRequiredFields = useMemo(() => {
    return state.confirmedFields.filter(
      (field) => fieldMetadata[field].required,
    );
  }, [state.confirmedFields]);

  const unconfirmedMappedFields = useMemo(() => {
    return Object.keys(state.columnMappings).filter(
      (field) =>
        field.charAt(0) !== "_" && !state.confirmedFields.includes(field as F),
    );
  }, [state.columnMappings, state.confirmedFields]);

  // set data validation alerts
  useEffect(() => {
    if (csvHeader && !mappingComplete) {
      setValidationAlerts(
        rowsWithInvalidValues.length
          ? [ALERTS.INVALID_VALUES]
          : [ALERTS.VALIDATION_SUCCESS],
      );
    } else {
      setValidationAlerts([]);
    }
  }, [csvHeader, mappingComplete, rowsWithInvalidValues]);

  // set data widget to validation when there are invalid values
  useEffect(() => {
    if (rowsWithInvalidValues.length > 0) {
      setDataWidget("validate");
    } else {
      setDataWidget("preview");
    }
  }, [rowsWithInvalidValues]);

  // set metadata alerts
  useEffect(() => {
    if (state.selectedSystemField && !mappingComplete) {
      setMetadataAlerts(fieldMetadata[state.selectedSystemField].alerts);
    } else {
      setMetadataAlerts([]);
    }
  }, [state.selectedSystemField, mappingComplete]);

  // enable next step when all required fields are confirmed
  useEffect(() => {
    if (confirmedRequiredFields.length === requiredFields.length) {
      dispatch({
        type: WIZARD_ACTION.SET_HAS_VALID_COLUMN_MAPPINGS,
        payload: true,
      });
    } else {
      dispatch({
        type: WIZARD_ACTION.SET_HAS_VALID_COLUMN_MAPPINGS,
        payload: false,
      });
    }
    if (mappingComplete) {
      setCenterAlerts([ALERTS.MAPPING_COMPLETE]);
      dispatch({
        type: WIZARD_ACTION.SET_SELECTED_SYSTEM_FIELD,
        payload: null,
      });
    }
  }, [confirmedRequiredFields, requiredFields, mappingComplete]);

  // set data widget to tutorial when no selected option
  useEffect(() => {
    if (!selectedOption) {
      setDataWidget("tutorial");
    }
  }, [selectedOption]);

  // set the selected option based on the current field
  useEffect(() => {
    if (!csvHeader) {
      setSelectedOption("");
    } else if (csvHeader.toLowerCase().startsWith("_generated")) {
      setSelectedOption("Generate Data");
    } else if (csvHeader.toLowerCase().includes("_property")) {
      setSelectedOption("Autofill Address");
    } else if (csvHeader.toLowerCase().includes("_vehicle")) {
      setSelectedOption("Smart Map Vehicle");
    } else if (csvHeader.toLowerCase().includes("_combined")) {
      setSelectedOption("Combine Fields");
    } else {
      setSelectedOption(csvHeader);
    }
  }, [csvHeader]);

  // button handlers and disabled states
  const handleSelectOption = (e: ChangeEvent<HTMLInputElement>) => {
    if (!state.selectedSystemField) return;
    const option = e.target.value;
    setSelectedOption(option);
    if (t(option) === "Generate Data") {
      setDataWidget("generate");
    } else if (t(option) === "Autofill Address") {
      // dispatch({ type: WIZARD_ACTION.AUTOFILL_ADDRESS }); // this needs to be better understood and implemented
    } else if (t(option) === "Smart Map Vehicle") {
      setDataWidget("smartMap");
    } else if (t(option) === "Combine Fields") {
      setDataWidget("combine");
    } else {
      dispatch({
        type: WIZARD_ACTION.APPEND_COLUMN_MAPPING,
        payload: {
          systemField: state.selectedSystemField,
          csvField: option,
        },
      });
    }
  };
  const handleConfirmFieldMapping = (systemField: F | null) => {
    if (!systemField || state.confirmedFields.includes(systemField)) {
      return;
    }
    dispatch({
      type: WIZARD_ACTION.CONFIRM_MAPPING,
      payload: systemField,
    });
    setSelectedOption("");
    setDataWidget("tutorial");
  };
  const confirmMappingIsDisabled = useMemo(() => {
    return (
      !state.selectedSystemField ||
      !csvHeader ||
      !selectedOption ||
      rowsWithInvalidValues.length > 0 ||
      state.confirmedFields.includes(state.selectedSystemField)
    );
  }, [
    state.selectedSystemField,
    csvHeader,
    selectedOption,
    rowsWithInvalidValues,
    state.confirmedFields,
  ]);
  const handleClearMapping = () => {
    if (!state.selectedSystemField || !csvHeader) return;
    dispatch({
      type: WIZARD_ACTION.CLEAR_FIELD_MAPPING,
      payload: {
        systemField: state.selectedSystemField,
        csvField: csvHeader,
      },
    });
    setSelectedOption("");
  };
  const clearMappingIsDisabled = useMemo(() => {
    return !state.selectedSystemField || !csvHeader;
  }, [state.selectedSystemField, csvHeader]);
  const handleValidateMappedFields = () => {
    dispatch({ type: WIZARD_ACTION.VALIDATE_ALL_MAPPED_FIELDS });
  };
  const validateAllIsDisabled = useMemo(() => {
    if (
      state.focusedRows.length > 0 ||
      unconfirmedMappedFields.length <= 0 ||
      !["preview", "tutorial"].includes(dataWidget)
    ) {
      return true;
    }
    return false;
  }, [state.focusedRows, selectedOption, unconfirmedMappedFields, dataWidget]);

  const renderDataWidget = (): ReactNode => {
    switch (dataWidget) {
      case "tutorial":
        return (
          <List disablePadding sx={{ overflowY: "hidden" }}>
            {centerAlerts.map((alert) => (
              <ImportAlert key={alert.title} {...alert} />
            ))}
          </List>
        );
      case "smartMap":
        return (
          <ExtractVehicleWidget
            setDataWidget={setDataWidget}
            setSelectedOption={setSelectedOption}
          />
        );
      case "validate":
        return (
          <ValidationWidget setDataWidget={() => setDataWidget("preview")} />
        );
      case "generate":
        return <GenerateDataWidget setDataWidget={setDataWidget} />;
      case "combine":
        return <CombineFieldsWidget setDataWidget={setDataWidget} />;
      case "preview":
        return <DataPreviewTable />;
      default:
        return null;
    }
  };

  return (
    <Stack direction="row" spacing={4} display="flex">
      <Stack direction="column" spacing={0} flex={2} height={maxHeight}>
        <Divider textAlign={"center"} role={"presentation"}>
          <Typography variant={"overline"}>
            {t("fieldList.title", { ns: "importWizard" })}
          </Typography>
        </Divider>
        <Typography variant="caption" width="100%" textAlign="center" pb={2}>
          {t("fieldList.progress", {
            ns: "importWizard",
            confirmedRequired: confirmedRequiredFields.length,
            totalRequired: requiredFields.length,
            confirmedTotal: state.confirmedFields.length,
            totalFields: state.entityFields.length,
          })}
        </Typography>
        <List disablePadding sx={{ overflowY: "scroll" }}>
          <ListSubheader disableGutters disableSticky>
            {t("fieldList.entity_fields", {
              ns: "importWizard",
              entity: capitalize(state.entityName),
            })}
          </ListSubheader>

          {/* general fields */}
          {generalFields.map((field) => (
            <SystemFieldListItem key={field} systemField={field} />
          ))}

          {/* vehicle fields */}
          {vehicleFields.length > 0 && (
            <ListSubheader disableGutters disableSticky>
              {t("fieldList.vehicle_fields", { ns: "importWizard" })}
            </ListSubheader>
          )}
          {vehicleFields.length > 0 &&
            vehicleFields.map((field) => (
              <SystemFieldListItem key={field} systemField={field} />
            ))}

          {/* contact fields */}
          {contactFields.length > 0 && (
            <ListSubheader disableGutters disableSticky>
              {t("fieldList.contact_fields", { ns: "importWizard" })}
            </ListSubheader>
          )}
          {contactFields.length > 0 &&
            contactFields.map((field) => (
              <SystemFieldListItem key={field} systemField={field} />
            ))}
          {/* location fields */}
          {locationFields.length > 0 && (
            <ListSubheader disableGutters disableSticky>
              {t("fieldList.location_fields", { ns: "importWizard" })}
            </ListSubheader>
          )}
          {locationFields.length > 0 &&
            locationFields.map((field) => (
              <SystemFieldListItem key={field} systemField={field} />
            ))}
        </List>
      </Stack>
      <Stack
        direction="column"
        flex={3}
        height={maxHeight}
        display="flex"
        flexDirection="column"
      >
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          height={15}
          flexShrink={0}
          alignContent="center"
          gap={2}
          px={3}
        >
          <Typography
            variant="subtitle2"
            width="100%"
            textAlign="right"
            gutterBottom
          >
            {t("importFileLabel", {
              name: state.fileName,
              ns: "importWizard",
            })}
          </Typography>
        </Box>
        <Box flexGrow={1} my={1} sx={{ overflow: "auto" }}>
          {state.selectedSystemField && !mappingComplete && (
            <ListItem
              disableGutters
              component="div"
              sx={{
                border: "2px solid",
                borderColor: "primary.main",
                borderRadius: 1,
                overflow: "hidden",
                mb: 2,
                display: "flex",
                flexDirection: "row",
                width: "100%",
                alignItems: "center",
                height: 50,
                "& .arrow-box, & .dropdown": {
                  transition: "background-color 0.3s ease-out",
                },
                "&:hover": {
                  "& .arrow-box, & .dropdown": {
                    backgroundColor: "#F6F6F6",
                    transition: "background-color 0.1s ease-in",
                  },
                },
              }}
            >
              <Box
                className="system-field"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: theme.palette.primary.main,
                  p: 2,
                  flex: 1,
                  position: "relative",
                }}
              >
                <Typography
                  sx={{
                    color: getContrastColor(theme.palette.primary.main),
                    flex: 1,
                  }}
                  variant="body1"
                  fontWeight="bold"
                >
                  {t(`${state.selectedSystemField}`, { ns: "entities" })}
                </Typography>
                <Box
                  className="arrow-box"
                  sx={{
                    position: "absolute",
                    right: -18,
                    backgroundColor: "white",
                    width: 45,
                    height: 45,
                    rotate: "45deg",
                    zIndex: 900,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <AuturaDropdown
                  name={"selectedOption"}
                  options={state.csvHeaders.filter(
                    (header) => header.toLowerCase().charAt(0) !== "_",
                  )}
                  value={selectedOption}
                  label={""}
                  setValue={handleSelectOption}
                  labelTextAlign="right"
                  menuItemProps={{
                    divider: true,
                    sx: {
                      textAlign: "right",
                      zIndex: 900,
                    },
                  }}
                  specialOptions={
                    fieldMetadata[state.selectedSystemField].specialOptions ||
                    []
                  }
                  className="dropdown"
                />
              </Box>
            </ListItem>
          )}
          {renderDataWidget()}
        </Box>
        <Stack
          direction="column"
          display="flex"
          flexShrink={0}
          spacing={1}
          height={88}
        >
          <Box
            display="flex"
            flexDirection="row"
            gap={4}
            justifyContent="space-between"
          >
            <Button
              variant="contained"
              color="error"
              onClick={handleClearMapping}
              sx={{ flex: 1 }}
              disabled={clearMappingIsDisabled}
            >
              {t("buttons.clear_mapping", { ns: "importWizard" })}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() =>
                handleConfirmFieldMapping(state.selectedSystemField)
              }
              sx={{ flex: 1 }}
              disabled={confirmMappingIsDisabled}
            >
              {t("buttons.confirm_mapping", { ns: "importWizard" })}
            </Button>
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            gap={4}
            justifyContent="space-between"
          >
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleValidateMappedFields}
              disabled={validateAllIsDisabled}
              sx={{ flex: 1 }}
            >
              {t("buttons.validate_all", { ns: "importWizard" })}
            </Button>
          </Box>
        </Stack>
      </Stack>
      <Stack
        direction="column"
        spacing={0}
        flex={2}
        height={maxHeight}
        sx={{ overflowY: "auto" }}
      >
        <Divider textAlign={"center"} role={"presentation"}>
          <Typography variant={"overline"}>
            {t("fieldInformation", { ns: "importWizard" })}
          </Typography>
        </Divider>
        <List disablePadding>
          {[...validationAlerts, ...metadataAlerts].map((alert, index) => (
            <ImportAlert key={index} {...alert} sx={{ mb: 2 }} />
          ))}
        </List>
      </Stack>
    </Stack>
  );
};

export default MapStep;
